import { apiSlice } from "./http";
import {
  ICampaignComparisonJobAccepted,
  ICampaignComparisonJobStatus,
  IGetCampaignComparisonParams,
} from "src/@types/campaign-comparison";

// `bust` só muda a chave de cache (não vai pro backend) — usado no "Atualizar".
type StartComparisonArgs = IGetCampaignComparisonParams & { bust?: number };

const isTerminal = (state?: string) =>
  state === "completed" || state === "failed";

export const campaignComparisonService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    startCampaignComparison: builder.query<
      ICampaignComparisonJobAccepted,
      StartComparisonArgs
    >({
      query: ({ type, code, page, perPage, storeCode, force }) => {
        const params = new URLSearchParams();
        if (page) params.set("page", String(page));
        if (perPage) params.set("perPage", String(perPage));
        if (storeCode) params.set("storeCode", String(storeCode));
        if (force) params.set("force", "true");
        const queryString = params.toString();

        return {
          url: `/campaigns/comparison/${type}/${code}/jobs${queryString ? `?${queryString}` : ""}`,
          method: "POST",
        };
      },
      keepUnusedDataFor: 600,
    }),

    // Polling do job via streaming (onCacheEntryAdded): refaz até o estado
    // terminal e para sozinho — o componente só assina.
    getCampaignComparisonJob: builder.query<ICampaignComparisonJobStatus, string>(
      {
        query: (jobId) => ({
          url: `/campaigns/comparison/jobs/${jobId}`,
          method: "GET",
        }),
        // Curto: ao sair da tela o polling do streaming para logo.
        keepUnusedDataFor: 60,
        async onCacheEntryAdded(
          jobId,
          { cacheDataLoaded, cacheEntryRemoved, getCacheEntry, dispatch },
        ) {
          const POLL_MS = 1500;
          let removed = false;
          void cacheEntryRemoved.then(() => {
            removed = true;
          });
          try {
            await cacheDataLoaded;
            while (!removed && !isTerminal(getCacheEntry().data?.state)) {
              await new Promise((resolve) => setTimeout(resolve, POLL_MS));
              if (removed) break;
              const refetch = dispatch(
                campaignComparisonService.endpoints.getCampaignComparisonJob.initiate(
                  jobId,
                  { forceRefetch: true },
                ),
              );
              try {
                await refetch;
              } finally {
                refetch.unsubscribe();
              }
            }
          } catch {
            // cacheEntryRemoved antes do primeiro load — nada a fazer.
          }
        },
      },
    ),
  }),
  overrideExisting: true,
});

export const {
  useStartCampaignComparisonQuery,
  useGetCampaignComparisonJobQuery,
} = campaignComparisonService;
